import { Modal, ModalContent } from 'pobble-components/dist/modal'
import React, { useState, useMemo, useEffect } from 'react'
import injectSheet from 'react-jss'
import Button from 'pobble-components/dist/button'
import { useMe } from 'pobble-components/dist/pobble-user'
import { apiClient } from 'pobble-components/dist/api'
import {
  useChargebeeScript,
  useSubscriptionDetails
} from 'pobble-components/dist/subscriptions/utils'
import { useHistory, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import duck from './duck-rocket.gif'
import posthog from 'posthog-js'
import edtechImpact from './edtech-impact.png'
import { ENV } from 'pobble-components/dist/setup'

const currencyMap = {
  AUD: 'A$',
  NZD: 'NZ$',
  USD: '$',
  GBP: '£'
}

const UpgradeButton = injectSheet({
  button: {
    margin: '0 auto',
    background: 'var(--yellow) !important',
    color: 'var(--gray) !important',
    border: '1px solid var(--gray) !important',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '320px',
    '&:hover': {
      background: 'var(--yellow-) !important'
    }
  }
})(({ id, classes, requestClose, children, freeTrial }) => {
  const { openCheckout } = useChargebeeScript()
  const history = useHistory()
  const location = useLocation()

  return (
    <Button
      minw
      darkBlue
      action={() => {
        requestClose()
        history.replace({
          pathname: location.pathname,
          search: `from=${id}`
        })
        posthog.capture('user clicked upsell modal cta', {
          id,
          add_trial: freeTrial
        })
        openCheckout({
          url: 'v3/me/subscriptions/checkout_new_pobble_silver_js',
          params: { annual: true, add_trial: freeTrial },
          options: {
            step: step => {
              posthog.capture('checkout step', {
                id,
                step
              })
            },
            close: () => {
              history.replace({
                pathname: location.pathname,
                search: false
              })
            }
          }
        })
      }}
      className={`${
        classes.button
      } metrics__subscription_modal metrics__subscription_modal--${id}`}
      tall>
      {children}
    </Button>
  )
})

const SchoolLeaders = injectSheet({
  title: {
    fontSize: '22px',
    lineHeight: '28px'
  },
  pitch: {
    font: 'var(--body-)',
    margin: '4px 0'
  },
  list: {
    flex: '1',
    padding: '0',
    listStyle: 'none',
    textAlign: 'left',
    font: 'var(--body)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '& li': {
      display: 'flex',
      gap: '10px'
    },
    '& svg': {
      color: 'var(--green)',
      fontSize: '20px'
    },
    '& span[role=img]': {
      marginTop: '2px'
    }
  },
  body: {
    display: 'flex',
    alignItems: 'center',
    margin: '36px 0 28px',
    flexDirection: 'column-reverse',
    gap: '48px',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      gap: '16px'
    }
  },
  price: {
    margin: 0,
    font: 'var(--heading_)'
  },
  priceNote: {
    margin: '16px auto',
    maxWidth: '440px'
  },
  edtech: {
    padding: '16px',
    borderTop: '1px solid var(--light-gray)',
    margin: '24px -32px -48px',
    '@media (min-width: 768px)': {
      margin: '24px -56px -48px'
    },
    '& img': {
      maxWidth: '200px',
      height: 'auto'
    }
  },
  button: {
    margin: '0 auto',
    background: 'var(--yellow) !important',
    color: 'var(--gray) !important',
    border: '1px solid var(--gray) !important',
    display: 'flex',
    justifyContent: 'center',
    width: '200px',
    '&:hover': {
      background: 'var(--yellow-) !important'
    }
  },
  buttonSecondary: {
    background: 'none !important',
    borderColor: 'var(--blue) !important',
    color: 'var(--blue) !important',
    '&:hover': {
      backgroundColor: 'rgba(0,175,236,0.5) !important',
      color: '#fff !important'
    }
  }
})(({ classes, requestClose, id, price }) => {
  const setLinkEnv = link => {
    return `${link}${ENV !== 'prod' ? '-stg' : ''}`
  }

  return (
    <>
      <h2 className={classes.title}>
        <span role="img" aria-label="star">
          🌟
        </span>{' '}
        Unlock Pobble Premium for your whole school!{' '}
        <span role="img" aria-label="star">
          🌟
        </span>
      </h2>
      <p className={classes.pitch}>
        The easiest and most effective way to improve writing standards across
        your school.
      </p>
      <div className={classes.body}>
        <ul className={classes.list}>
          <li>
            <span role="img" aria-label="clock">
              ⏰
            </span>
            <span>
              <strong> Save planning time:</strong> Provide every teacher with
              unlimited access to high-quality, ready-made writing lessons that
              can seamlessly enhance your existing English plans.
            </span>
          </li>
          <li>
            <span role="img" aria-label="check">
              ✅
            </span>
            <span>
              <strong> Curriculum-aligned:</strong> Pobble aligns with all
              curriculums and is simple to set up without requiring additional
              CPD for staff.
            </span>
          </li>
          <li>
            <span role="img" aria-label="lightbulb">
              💡
            </span>
            <span>
              <strong> Inspire every student:</strong> Motivate every child to
              write with enthusiasm and confidence using differentiated
              activities designed for diverse learners.
            </span>
          </li>
          <li>
            <span role="img" aria-label="heart">
              💛
            </span>
            <span>
              <strong> Embed a school-wide love of writing:</strong> Make
              writing exciting for students while saving teachers hours of
              planning time.
            </span>
          </li>
        </ul>
        <img src={duck} alt="Duck" width="175" height="175" />
      </div>
      <div>
        <p className={classes.priceNote}>
          All this and more for only{' '}
          <strong>
            {!price ? '...' : `${price.currency}${price.schoolPrice}/year`}
          </strong>!<br />Ready to improve writing in your school? 🪄📝
        </p>
      </div>
      <div>
        <Button
          minw
          darkBlue
          _blank
          href={'https://4oqv9.share.hsforms.com/2yIHCHPh5TFGK9vy3CBUGoQ'}
          action={() => {
            posthog.capture('clicked on school leaders upsell cta')
            requestClose()
          }}
          className={`${
            classes.button
          } metrics__subscription_modal metrics__subscription_modal--${id}`}
          tall>
          Start your free trial
        </Button>
        <br />
        <Button
          minw
          darkBlue
          _blank
          href={
            'https://7873029.fs1.hubspotusercontent-na1.net/hubfs/7873029/Pobble%20-%20School%20Pack.pdf'
          }
          action={() => {
            posthog.capture('clicked on info pack link')
            requestClose()
          }}
          className={`${classes.button} ${
            classes.buttonSecondary
          } metrics__subscription_modal metrics__subscription_modal--${id}`}
          simple
          tall>
          Download your info pack
        </Button>
      </div>
      <div className={classes.edtech}>
        <a
          href="https://edtechimpact.com/products/pobble-planning/"
          rel="noopener noreferrer"
          target="_blank">
          <img
            width={543}
            height={75}
            src={edtechImpact}
            alt="Ed Tech Impact score is 4.8/5"
          />
        </a>
      </div>
    </>
  )
})

const UnlockPremium = injectSheet({
  title: {
    fontSize: '22px',
    lineHeight: '28px',
    paddingBottom: '12px'
  },
  pitch: {
    font: 'var(--body)',
    margin: '0 0 28px',
    color: 'var(--gray)'
  },
  list: {
    margin: '0 auto 32px',
    padding: '0',
    listStyle: 'none',
    textAlign: 'left',
    font: 'var(--body)',
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '12px',
    '& li': {
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
      color: 'var(--gray-)'
    },
    '& svg': {
      color: 'var(--blue)',
      fontSize: '16px',
      flexShrink: 0
    }
  }
})(({ classes, price, requestClose, id, freeTrial }) => {
  return (
    <>
      <h2 className={classes.title}>
        Unlock Pobble Premium
        <br />
        to access this feature
      </h2>
      <p className={classes.pitch}>
        Get unlimited access for{' '}
        <strong>
          {!price ? '...' : `${price.currency}${price.annualPrice}/year`}
        </strong>{' '}
        per teacher
      </p>
      <ul className={classes.list}>
        <li>
          <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
          Unlimited access to the Pobble 365 calendar
        </li>
        <li>
          <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
          Every lesson at three levels: Original, Easier, Harder
        </li>
        <li>
          <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
          Themed lessons and quick writes
        </li>
        <li>
          <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
          Save favourites for later
        </li>
        <li>
          <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
          Edit and adapt any lesson
        </li>
        <li>
          <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
          Share lessons
        </li>
      </ul>
      <UpgradeButton freeTrial={freeTrial} id={id} requestClose={requestClose}>
        Unlock Pobble Premium
      </UpgradeButton>
    </>
  )
})

// Kept for potential future use
const FreeTrial = injectSheet({
  wrapper: {
    textAlign: 'center'
  },
  subtitle: {
    font: 'var(--body)',
    color: 'var(--gray-)',
    marginBottom: '24px'
  },
  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '@media (min-width: 768px)': {
      flexDirection: 'row'
    }
  },
  card: {
    flex: 1,
    border: '1px solid var(--light-gray)',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  cardPrimary: {
    border: '2px solid var(--blue)',
    background: 'rgba(8, 166, 241, 0.03)'
  },
  cardHeader: {
    fontWeight: 'bold',
    fontSize: '16px'
  },
  cardList: {
    padding: 0,
    margin: 0,
    listStyle: 'none',
    font: 'var(--body)',
    '& li': {
      marginBottom: '6px'
    }
  },
  cardCTA: {
    marginTop: 'auto'
  },
  cardSmall: {
    fontSize: '12px',
    color: 'var(--gray-)'
  },
  footer: {
    marginTop: '24px',
    font: 'var(--body-)',
    color: 'var(--gray-)'
  }
})(({ classes, requestClose, id }) => {
  return (
    <>
      <div className={classes.wrapper}>
        <h2>Unlock Pobble Premium</h2>

        <p className={classes.subtitle}>
          ✨ Ready to unlock this Premium activity? ✨
          <br />
          Choose the option that works best for you.
        </p>

        <div className={classes.cards}>
          {/* TRIAL CARD */}
          <div className={`${classes.card} ${classes.cardPrimary}`}>
            <div className={classes.cardHeader}>Try Premium for free 🤩</div>

            <div>Get 7 days to explore Premium.</div>

            <ul className={classes.cardList}>
              <li>• Full access for 7 days</li>
              <li>• Time to look around</li>
              <li>• Cancel any time</li>
            </ul>

            <div className={classes.cardCTA}>
              <UpgradeButton
                freeTrial={true}
                id={id}
                requestClose={requestClose}>
                Start my free trial
              </UpgradeButton>
            </div>

            <div className={classes.cardSmall}>
              Credit card required. Cancel anytime
            </div>
          </div>

          {/* DISCOUNT CARD */}
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              Go Premium now, save 40% 💡
            </div>

            <div>Get instant access at our lowest Premium price.</div>

            <ul className={classes.cardList}>
              <li>• No trial needed</li>
              <li>• Save 40%</li>
              <li>• Lowest Premium price</li>
            </ul>

            <div className={classes.cardCTA}>
              <UpgradeButton
                id={`${id}-discount`}
                freeTrial={false}
                requestClose={requestClose}>
                Claim my 40% off
              </UpgradeButton>
            </div>

            <div className={classes.cardSmall}>Limited-time offer</div>
          </div>
        </div>

        <div className={classes.footer}>
          🌎 Made by teachers, trusted by over a million educators in 70+
          countries 🌎
        </div>
      </div>
    </>
  )
})

const HadTrialBefore = injectSheet({
  pitch: {
    font: 'var(--body-)',
    margin: '4px 0'
  },
  list: {
    flex: '1',
    padding: '0',
    listStyle: 'none',
    textAlign: 'left',
    font: 'var(--body)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '& li': {
      display: 'flex',
      gap: '10px',
      fontWeight: 'bold',
      alignItems: 'center'
    },
    '& svg': {
      color: 'var(--green)',
      fontSize: '20px'
    }
  },
  body: {
    display: 'flex',
    alignItems: 'center',
    margin: '56px 0 48px',
    flexDirection: 'column-reverse',
    gap: '48px',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      gap: '16px'
    }
  },
  priceNote: {
    font: 'var(--body-)',
    color: 'var(--gray-)',
    margin: '16px auto 0',
    lineHeight: '24px'
  }
})(({ classes, price, requestClose, id }) => {
  return (
    <>
      <h2>
        <span role="img" aria-label="star">
          🌟
        </span>{' '}
        Unlock your ultimate writing experience with Pobble Premium!{' '}
        <span role="img" aria-label="star">
          🌟
        </span>
      </h2>
      <p className={classes.pitch}>
        Are you ready to elevate your teaching and inspire young writers like
        never before? Discover the power of Pobble Premium
      </p>
      <div className={classes.body}>
        <ul className={classes.list}>
          <li>
            <span role="img" aria-label="calendar">
              🗓️
            </span>{' '}
            Full access to the Pobble 365 Calendar and differentiated
            activities.
          </li>
          <li>
            <span role="img" aria-label="writing">
              📝
            </span>{' '}
            Topic and quick write lessons.
          </li>
          <li>
            <span role="img" aria-label="lightbulb">
              🔖
            </span>{' '}
            Save your favourites and create a bank of writing lessons.
          </li>
          <li>
            <span role="img" aria-label="lightbulb">
              💡
            </span>{' '}
            Plus, you can edit and share unlimited writing content!
          </li>
        </ul>
        <img src={duck} alt="Duck" width="175" height="175" />
      </div>
      <div>
        All this and more, for just{' '}
        {price && `${price.currency}${price.price}/month`}!<br />
        <span role="img" aria-label="rocket">
          🚀
        </span>{' '}
        Don't miss out – upgrade now!{' '}
        <span role="img" aria-label="rocket">
          🚀
        </span>
      </div>
      <UpgradeButton freeTrial={false} id={id} requestClose={requestClose}>
        Upgrade now
      </UpgradeButton>
    </>
  )
})

const Upsell = injectSheet({
  content: {
    maxWidth: '680px',
    padding: '0',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  holder: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: '8px',
    '& > *': {
      padding: '48px 32px',
      '@media (min-width: 768px)': {
        padding: '48px 56px'
      }
    }
  },
  signIn: {
    padding: '20px 32px',
    textAlign: 'center',
    background: '#f5f5f5',
    '& a': {
      color: 'var(--blue)',
      fontWeight: 'bold',
      textDecoration: 'underline'
    }
  },
  tier: {
    background: '#f0f0f0',
    '& h3': {
      textAlign: 'center',
      margin: '0 0 48px'
    },
    '@media (min-width: 768px)': {
      width: '100%'
    }
  },
  upsell: {
    font: 'var(--body)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '& h2': {
      margin: '0',
      fontSize: '22px',
      lineHeight: '26px'
    }
  },
  price: {
    textAlign: 'center',
    margin: '0 0 48px',
    height: '84px',
    '& p': {
      margin: '0'
    },
    '& p:first-child': {
      position: 'relative',
      display: 'inline-block'
    }
  },
  currency: {
    position: 'absolute',
    top: '4px',
    right: '100%',
    marginRight: '-4px'
  },
  priceNumber: {
    fontSize: '64px',
    lineHeight: '1.2'
  },
  priceNote: {
    font: 'var(--body-)',
    color: 'var(--gray-)',
    margin: '16px auto 0',
    lineHeight: '24px'
  },
  spinner: {
    color: '#fff',
    fontSize: '16px',
    margin: 'auto'
  },
  pitch: {
    font: 'var(--body-)',
    margin: '4px 0'
  },
  list: {
    flex: '1',
    padding: '0',
    listStyle: 'none',
    textAlign: 'left',
    font: 'var(--body)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '& li': {
      display: 'flex',
      gap: '10px',
      fontWeight: 'bold',
      alignItems: 'center'
    },
    '& svg': {
      color: 'var(--green)',
      fontSize: '20px'
    }
  },
  body: {
    display: 'flex',
    alignItems: 'center',
    margin: '56px 0 48px',
    flexDirection: 'column-reverse',
    gap: '48px',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      gap: '16px'
    }
  }
})(({ classes, children, id, requestClose, hideCta }) => {
  const { me } = useMe()
  const { offerFreeTrial } = useSubscriptionDetails()
  const isLoggedIn = !!me

  const [subsData, setSubsData] = useState(null)

  const price = useMemo(
    () => {
      if (!subsData) return null
      return {
        currency: currencyMap[subsData.currency],
        price: parseInt(subsData.pobble_silver_annual) / 12,
        annualPrice: parseInt(subsData.pobble_silver_annual),
        schoolPrice: parseInt(subsData.pobble_school_a_annual)
      }
    },
    [subsData]
  )

  useEffect(() => {
    apiClient
      .get('v3/subscriptions/marketing_data')
      .then(res => setSubsData(res.data.subscription_marketing_data))
  }, [])

  useEffect(
    () => {
      posthog.capture('user opened upsell modal', {
        id: id
      })
    },
    [id]
  )

  const isSchoolRepresentative =
    me.account.role_in_life === 'school_administrator' ||
    me.account.role_in_life === 'english_leader_in_school'

  return (
    <ModalContent className={classes.content}>
      <div className={classes.holder}>
        <div className={classes.upsell}>
          {isSchoolRepresentative ? (
            <SchoolLeaders id={id} requestClose={requestClose} price={price} />
          ) : (
            <UnlockPremium
              id={id}
              requestClose={requestClose}
              price={price}
              freeTrial={offerFreeTrial}
            />
          )}
        </div>
      </div>
      {!isLoggedIn && (
        <div className={classes.signIn}>
          Already have a subscription? <a href="/auth/">Sign in</a>
        </div>
      )}
    </ModalContent>
  )
})

export const UpsellModal = ({ isOpen, requestClose, ...props }) => {
  return (
    <Modal closeButton isOpen={isOpen} requestClose={requestClose}>
      <Upsell {...props} requestClose={requestClose} />
    </Modal>
  )
}
